import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Sparkles, Briefcase, Phone, Upload, CheckCircle2, Send, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RESUME_MAX_BYTES = 5 * 1024 * 1024;
const RESUME_ACCEPT = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const validPhone = (raw) => (raw || '').replace(/\D+/g, '').length >= 10;
const validEmail = (raw) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(raw || '');

export const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [storePhone, setStorePhone] = useState('(905) 847-8533');
  const [selectedJob, setSelectedJob] = useState('Party Host');
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', availability: '', experience: '',
  });
  const [resume, setResume] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { application_id, job_title }

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/careers/jobs`);
        setJobs(res.data.jobs || []);
        setStorePhone(res.data.store_phone || '(905) 847-8533');
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const pickJob = (title) => {
    setSelectedJob(title);
    setSuccess(null);
    setTimeout(() => {
      document.getElementById('careers-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const onResumeChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setResume(null); return; }
    const ext = f.name.toLowerCase().match(/\.(pdf|doc|docx)$/);
    if (!ext) { toast.error('Resume must be PDF, DOC, or DOCX'); e.target.value = ''; return; }
    if (f.size > RESUME_MAX_BYTES) { toast.error('Resume must be under 5MB'); e.target.value = ''; return; }
    setResume(f);
  };

  const validate = () => {
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = 'Name is required';
    if (!validEmail(form.email)) errs.email = 'Enter a valid email';
    if (!validPhone(form.phone)) errs.phone = 'Enter a valid phone (10 digits)';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('job_title', selectedJob);
      fd.append('full_name', form.full_name);
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      if (form.availability) fd.append('availability', form.availability);
      if (form.experience) fd.append('experience', form.experience);
      if (resume) fd.append('resume', resume);
      const res = await axios.post(`${API_URL}/api/careers/apply`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess({ application_id: res.data.application_id, job_title: selectedJob });
      setForm({ full_name: '', email: '', phone: '', availability: '', experience: '' });
      setResume(null);
      toast.success('Application received!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const staffJob = jobs.find((j) => j.title === 'Staff');

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />

      <div className="max-w-6xl mx-auto px-4 py-10 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-[#FF3B6B]" />
            <p className="text-sm tracking-wide text-[#6B6B7A] uppercase">Careers</p>
          </div>
          <h1 className="text-4xl sm:text-6xl font-semibold text-[#1E1E24] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Work at Creative Birthdays — join the fun!
          </h1>
          <p className="text-[#6B6B7A] max-w-2xl mb-10">
            We hire energetic, kind people who genuinely love making a kid's big day unforgettable. Pick a role below to learn more and apply in under two minutes.
          </p>

          {/* Job cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
            {jobs.map((j) => {
              const isSelected = selectedJob === j.title;
              return (
                <motion.article
                  key={j.title}
                  whileHover={{ y: -3 }}
                  transition={{ duration: 0.2 }}
                  className={`bg-white rounded-3xl border ${isSelected ? 'border-[#FF3B6B]' : 'border-[#E8E5DF]'} p-6 sm:p-7 shadow-sm flex flex-col`}
                  data-testid={`job-card-${j.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-11 h-11 rounded-2xl bg-[#FFE8EC] flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-[#FF3B6B]" />
                    </div>
                    {j.apply_methods?.includes('phone') && (
                      <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-[#FFD166]/30 text-[#9C6B12]">CALL OR APPLY ONLINE</span>
                    )}
                  </div>
                  <h2 className="text-2xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{j.title}</h2>
                  <p className="text-sm text-[#FF3B6B] font-medium mb-3">{j.summary}</p>
                  <p className="text-sm text-[#6B6B7A] leading-relaxed mb-4">{j.description}</p>
                  <ul className="space-y-1.5 mb-5">
                    {(j.key_points || []).map((k, i) => (
                      <li key={i} className="text-sm text-[#1E1E24] flex items-start gap-2">
                        <span className="text-[#06D6A0] mt-0.5">✓</span> {k}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto flex gap-2 flex-wrap">
                    <Button
                      onClick={() => pickJob(j.title)}
                      className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
                      data-testid={`apply-btn-${j.title.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      Apply online <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                    {j.apply_methods?.includes('phone') && (
                      <a
                        href={`tel:${storePhone.replace(/\D+/g, '')}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E8E5DF] hover:border-[#FF3B6B] text-sm text-[#1E1E24] transition-colors"
                        data-testid={`call-btn-${j.title.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Phone className="w-4 h-4" /> {storePhone}
                      </a>
                    )}
                  </div>
                </motion.article>
              );
            })}
          </div>

          {staffJob?.apply_methods?.includes('phone') && (
            <p className="text-sm text-[#6B6B7A] mb-8 bg-[#FFF4E6] border border-[#FFD166] rounded-xl p-3" data-testid="call-hours-note">
              Prefer to chat? Call <strong>{storePhone}</strong> between 10am-6pm to speak with a manager directly.
            </p>
          )}

          {/* Application form */}
          <div id="careers-form" className="bg-white rounded-3xl border border-[#E8E5DF] p-6 sm:p-8 shadow-sm">
            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
                data-testid="careers-success"
              >
                <CheckCircle2 className="w-14 h-14 text-[#06D6A0] mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Application received!
                </h2>
                <p className="text-[#6B6B7A] mb-1">
                  Thanks for applying for the <strong>{success.job_title}</strong> role.
                </p>
                <p className="text-xs text-[#6B6B7A] font-mono mb-6">Ref: {success.application_id}</p>
                <Button
                  onClick={() => { setSuccess(null); pickJob(selectedJob); }}
                  variant="outline"
                  className="rounded-full border-[#E8E5DF]"
                  data-testid="careers-apply-again"
                >
                  Apply for another role
                </Button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="careers-form">
                <h2 className="text-2xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Apply for {selectedJob}
                </h2>

                <div>
                  <Label className="text-sm font-medium text-[#1E1E24]">Role</Label>
                  <Select value={selectedJob} onValueChange={setSelectedJob}>
                    <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="careers-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.map((j) => (
                        <SelectItem key={j.title} value={j.title}>{j.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="c-name" className="text-sm font-medium text-[#1E1E24]">Full name</Label>
                  <Input
                    id="c-name" value={form.full_name} onChange={(e) => update('full_name', e.target.value)}
                    className={`mt-1 rounded-xl ${errors.full_name ? 'border-[#FF3B6B]' : 'border-[#E8E5DF]'}`}
                    placeholder="Alex Applicant" maxLength={120}
                    data-testid="careers-input-name"
                  />
                  {errors.full_name && <p className="text-xs text-[#FF3B6B] mt-1" data-testid="careers-error-name">{errors.full_name}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="c-email" className="text-sm font-medium text-[#1E1E24]">Email</Label>
                    <Input
                      id="c-email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                      className={`mt-1 rounded-xl ${errors.email ? 'border-[#FF3B6B]' : 'border-[#E8E5DF]'}`}
                      placeholder="you@example.com"
                      data-testid="careers-input-email"
                    />
                    {errors.email && <p className="text-xs text-[#FF3B6B] mt-1" data-testid="careers-error-email">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className="text-sm font-medium text-[#1E1E24]">Phone</Label>
                    <Input
                      id="c-phone" type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)}
                      className={`mt-1 rounded-xl ${errors.phone ? 'border-[#FF3B6B]' : 'border-[#E8E5DF]'}`}
                      placeholder="(416) 555-0100"
                      data-testid="careers-input-phone"
                    />
                    {errors.phone && <p className="text-xs text-[#FF3B6B] mt-1" data-testid="careers-error-phone">{errors.phone}</p>}
                  </div>
                </div>

                <div>
                  <Label htmlFor="c-avail" className="text-sm font-medium text-[#1E1E24]">Availability</Label>
                  <Textarea
                    id="c-avail" value={form.availability} onChange={(e) => update('availability', e.target.value)}
                    rows={2} placeholder="e.g. Weekends after 12pm + Thursday evenings"
                    className="mt-1 rounded-xl border-[#E8E5DF]"
                    data-testid="careers-input-availability"
                  />
                </div>

                <div>
                  <Label htmlFor="c-exp" className="text-sm font-medium text-[#1E1E24]">Relevant experience</Label>
                  <Textarea
                    id="c-exp" value={form.experience} onChange={(e) => update('experience', e.target.value)}
                    rows={4} placeholder="Tell us about any experience working with kids, in kitchens, or running events."
                    className="mt-1 rounded-xl border-[#E8E5DF]"
                    data-testid="careers-input-experience"
                  />
                </div>

                <div>
                  <Label htmlFor="c-resume" className="text-sm font-medium text-[#1E1E24]">Resume (optional, PDF/DOC/DOCX, max 5MB)</Label>
                  <div className="mt-1 flex items-center gap-3">
                    <label
                      htmlFor="c-resume"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E8E5DF] hover:border-[#FF3B6B] cursor-pointer text-sm text-[#1E1E24] transition-colors"
                    >
                      <Upload className="w-4 h-4" /> Choose file
                    </label>
                    <input
                      id="c-resume" type="file" accept={RESUME_ACCEPT}
                      onChange={onResumeChange} className="hidden"
                      data-testid="careers-input-resume"
                    />
                    <span className="text-sm text-[#6B6B7A] truncate" data-testid="careers-resume-filename">
                      {resume ? resume.name : 'No file selected'}
                    </span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full py-6 text-base"
                  data-testid="careers-submit-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Submitting…' : 'Submit Application'}
                </Button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Careers;
